'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  DEFAULT_CHAT_HISTORY_PAGE_SIZE,
  type FetchChatHistoryPage,
} from '../types/paginated-chat-history';

const MAX_INITIAL_HISTORY_LOAD_ATTEMPTS = 2;

const isSeedAssistantMessage = (message: TerraUIMessage) =>
  message.id === '' && message.role === 'assistant';

const getHasMoreWithCursor = ({
  hasMore,
  nextCursor,
}: {
  hasMore: boolean;
  nextCursor: number | null;
}) => hasMore && nextCursor !== null;

export function usePaginatedChatHistory({
  enabled,
  threadId,
  messages,
  setMessages,
  fetchPage,
  pageSize = DEFAULT_CHAT_HISTORY_PAGE_SIZE,
  initialNextCursor = null,
  initialHasMore = false,
  skipInitialFetch = false,
  onPaginationChange,
}: {
  enabled: boolean;
  threadId: string;
  messages: TerraUIMessage[];
  setMessages: (messages: TerraUIMessage[]) => void;
  fetchPage: FetchChatHistoryPage;
  pageSize?: number;
  initialNextCursor?: number | null;
  initialHasMore?: boolean;
  skipInitialFetch?: boolean;
  onPaginationChange?: (state: {
    nextCursor: number | null;
    hasMore: boolean;
  }) => void;
}) {
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const setMessagesRef = useRef(setMessages);
  setMessagesRef.current = setMessages;

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const onPaginationChangeRef = useRef(onPaginationChange);
  onPaginationChangeRef.current = onPaginationChange;

  const nextCursorRef = useRef<number | null>(initialNextCursor);
  const loadedForKeyRef = useRef<string | null>(null);
  const initialLoadAttemptsRef = useRef({
    key: null as string | null,
    count: 0,
  });
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoadRetry, setInitialLoadRetry] = useState(0);

  useEffect(() => {
    if (!enabled || !skipInitialFetch || !threadId) {
      return;
    }

    nextCursorRef.current = initialNextCursor;
    setHasMore(
      getHasMoreWithCursor({
        hasMore: initialHasMore,
        nextCursor: initialNextCursor,
      }),
    );
  }, [
      enabled,
    initialHasMore,
    initialNextCursor,
    skipInitialFetch,
    threadId,
  ]);

  useEffect(() => {
    if (!enabled || !threadId) {
      return;
    }

    const instanceKey = threadId;
    if (initialLoadAttemptsRef.current.key !== instanceKey) {
      initialLoadAttemptsRef.current = { key: instanceKey, count: 0 };
    }

    if (loadedForKeyRef.current === instanceKey) {
      return;
    }

    nextCursorRef.current = initialNextCursor;
    setHasMore(
      getHasMoreWithCursor({
        hasMore: initialHasMore,
        nextCursor: initialNextCursor,
      }),
    );

    if (skipInitialFetch) {
      loadedForKeyRef.current = instanceKey;
      return;
    }

    setIsInitialLoading(true);
    let cancelled = false;

    const loadInitialPage = async () => {
      try {
        const page = await fetchPageRef.current({
          threadId,
                  limit: pageSize,
        });

        if (cancelled) return;

        if (!page) {
          setHasMore(false);
          nextCursorRef.current = null;
          loadedForKeyRef.current = instanceKey;
          onPaginationChangeRef.current?.({ nextCursor: null, hasMore: false });
          return;
        }

        if (page.history.length > 0) {
          const currentMessages = messagesRef.current;
          if (
            currentMessages.length === 0 ||
            currentMessages.every(isSeedAssistantMessage)
          ) {
            setMessagesRef.current(page.history);
          } else {
            const pageIds = new Set(page.history.map((m) => m.id));
            const localOnlyMessages = currentMessages.filter(
              (m) => !pageIds.has(m.id),
            );
            if (localOnlyMessages.length !== currentMessages.length) {
              setMessagesRef.current([...page.history, ...localOnlyMessages]);
            } else {
              setMessagesRef.current([...page.history, ...currentMessages]);
            }
          }
        }

        const nextHasMore = getHasMoreWithCursor({
          hasMore: page.hasMore,
          nextCursor: page.nextCursor,
        });
        nextCursorRef.current = page.nextCursor;
        setHasMore(nextHasMore);
        loadedForKeyRef.current = instanceKey;
        onPaginationChangeRef.current?.({
          nextCursor: page.nextCursor,
          hasMore: nextHasMore,
        });
      } catch (error: unknown) {
        console.warn(
          '[usePaginatedChatHistory] Initial page fetch failed',
          error,
        );
        if (!cancelled) {
          loadedForKeyRef.current = null;
          initialLoadAttemptsRef.current.count += 1;
          if (
            initialLoadAttemptsRef.current.count <
            MAX_INITIAL_HISTORY_LOAD_ATTEMPTS
          ) {
            setInitialLoadRetry((retry) => retry + 1);
          }
        }
      } finally {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    };

    void loadInitialPage();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    threadId,
      skipInitialFetch,
    initialNextCursor,
    initialHasMore,
    pageSize,
    initialLoadRetry,
  ]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !threadId) {
      return;
    }

    const cursor = nextCursorRef.current;
    if (cursor === null) {
      setHasMore(false);
      onPaginationChangeRef.current?.({ nextCursor: null, hasMore: false });
      return;
    }

    setIsLoadingMore(true);

    try {
      const page = await fetchPageRef.current({
        threadId,
              limit: pageSize,
        cursor,
      });

      if (!page || page.history.length === 0) {
        setHasMore(false);
        nextCursorRef.current = null;
        onPaginationChangeRef.current?.({ nextCursor: null, hasMore: false });
        return;
      }

      const currentMessages = messagesRef.current;
      const existingIds = new Set(currentMessages.map((m) => m.id));
      const newMessages = page.history.filter((m) => !existingIds.has(m.id));
      if (newMessages.length > 0) {
        setMessagesRef.current([...newMessages, ...currentMessages]);
      }
      const nextHasMore = getHasMoreWithCursor({
        hasMore: page.hasMore,
        nextCursor: page.nextCursor,
      });
      nextCursorRef.current = page.nextCursor;
      setHasMore(nextHasMore);
      onPaginationChangeRef.current?.({
        nextCursor: page.nextCursor,
        hasMore: nextHasMore,
      });
    } catch (error: unknown) {
      console.warn('[usePaginatedChatHistory] loadMore failed', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, pageSize, threadId]);

  const resetPagination = useCallback(() => {
    nextCursorRef.current = null;
    setHasMore(false);
    onPaginationChangeRef.current?.({ nextCursor: null, hasMore: false });
  }, []);

  return {
    hasMore,
    isInitialLoading,
    isLoadingMore,
    loadMore,
    resetPagination,
    nextCursor: nextCursorRef.current,
  };
}
